import { bridge } from '../bridge/native_bridge.ts';
import {
    collectDiagnostics,
    probeCodecs,
    probeSandbox,
    type CodecSupport,
    type Diagnostics,
    type SandboxProbe,
} from './diagnostics.ts';
import { deviceKeys } from '../secrets/device_keys.ts';
import { exportPublicKey, fingerprintOf } from '../secrets/keys.ts';
import { runEchoTest, type EchoTestOptions, type EchoTestResult } from './echo_test.ts';
import { videoSpike } from './video_spike.ts';

// Spike-only scaffolding (thrown away at the gate): one report per device for
// docs/spike-results.md, shown on screen and readable by scripts/spike-report.mjs.

export interface SpikeReport {
    diagnostics: Diagnostics;
    bridge: boolean;
    codecs: CodecSupport[];
    sandbox: SandboxProbe;
    echo?: EchoTestResult;
}

const globals = globalThis as unknown as Record<string, unknown>;

export async function capabilityReport() : Promise<SpikeReport> {
    const [codecs, sandbox] = await Promise.all([probeCodecs(globals), probeSandbox()]);

    return { diagnostics: collectDiagnostics(globals), bridge: bridge !== null, codecs, sandbox };
}

export async function echoTest(options: EchoTestOptions) : Promise<EchoTestResult> {
    if (!bridge) {
        return {
            ok: false,
            bytes: options.bytes,
            received: 0,
            openMs: 0,
            roundTripMs: 0,
            mbitPerSecond: 0,
            error: 'no native bridge in this host',
        };
    }
    return runEchoTest(bridge, options);
}

/** Called over the DevTools protocol by scripts/spike-report.mjs. */
globals.__casaSpike = {
    async report(echo?: EchoTestOptions) : Promise<SpikeReport> {
        const report = await capabilityReport();

        return echo ? { ...report, echo: await echoTest(echo) } : report;
    },
    /** Called by scripts/video-report.mjs: `start(url?)`, `stop()`, `state()`. */
    video: videoSpike,
    /** This device's key's short code (secrets SE-12): the same after a restart while the shell's IndexedDB keeps the key (QSE-5). */
    async deviceKey() : Promise<string> {
        return fingerprintOf(await exportPublicKey((await deviceKeys()).publicKey));
    },
};
